'use client';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { X, SlidersHorizontal } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

interface FilterState {
  categories: string[];
  languages: string[];
  tiers: string[];
  frameworks: string[];
}

interface FilterPanelProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onClear: () => void;
  className?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

const FILTER_OPTIONS = {
  categories: [
    'React',
    'Vue',
    'Angular',
    'Python',
    'JavaScript',
    'TypeScript',
    'Backend',
    'Frontend',
    'DevOps',
    'Database',
  ],
  languages: ['JavaScript', 'TypeScript', 'Python', 'Go', 'Rust', 'Java'],
  tiers: ['S', 'A', 'B', 'C'],
  frameworks: ['Next.js', 'React', 'Vue', 'Express', 'Django', 'FastAPI'],
};

export function FilterPanel({
  filters,
  onFilterChange,
  onClear,
  className = '',
  isOpen = true,
  onToggle,
}: FilterPanelProps) {
  const activeCount = calculateActiveCount(filters);

  const handleToggle = (
    filterType: keyof FilterState,
    value: string
  ) => {
    const currentValues = filters[filterType];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];

    onFilterChange({
      ...filters,
      [filterType]: newValues,
    });
  };

  const handleBackdropClick = () => {
    if (onToggle) {
      onToggle();
    }
  };

  // Prevent body scroll when mobile filter is open
  useEffect(() => {
    if (isOpen && onToggle) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onToggle]);

  const filterContent = (
    <div className={`space-y-6 ${className}`} role="region" aria-label="Pattern filters">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5 text-gray-400" aria-hidden="true" />
          <h2 id="filter-heading" className="text-lg font-semibold text-white">Filters</h2>
          {activeCount > 0 && (
            <Badge variant="info" size="sm" aria-label={`${activeCount} active filters`}>
              {activeCount}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={activeCount === 0}
            iconLeft={<X className="h-4 w-4" />}
            aria-label="Clear all filters"
          >
            Clear
          </Button>
          {/* Mobile close button */}
          {onToggle && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="md:hidden"
              aria-label="Close filters panel"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Categories */}
      <FilterSection
        title="Categories"
        options={FILTER_OPTIONS.categories}
        selectedValues={filters.categories}
        onToggle={(value) => handleToggle('categories', value)}
      />

      {/* Languages */}
      <FilterSection
        title="Languages"
        options={FILTER_OPTIONS.languages}
        selectedValues={filters.languages}
        onToggle={(value) => handleToggle('languages', value)}
      />

      {/* Tiers */}
      <FilterSection
        title="Tiers"
        options={FILTER_OPTIONS.tiers}
        selectedValues={filters.tiers}
        onToggle={(value) => handleToggle('tiers', value)}
      />

      {/* Frameworks */}
      <FilterSection
        title="Frameworks"
        options={FILTER_OPTIONS.frameworks}
        selectedValues={filters.frameworks}
        onToggle={(value) => handleToggle('frameworks', value)}
      />
    </div>
  );

  // Desktop view (always visible)
  if (!onToggle) {
    return filterContent;
  }

  // Mobile view with toggle functionality
  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Filter Panel */}
      <motion.div
        initial={false}
        animate={{
          x: isOpen ? 0 : '-100%',
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
        }}
        className="fixed left-0 top-0 bottom-0 w-80 bg-gray-800 border-r border-gray-700 z-50 overflow-y-auto p-6 md:relative md:translate-x-0 md:w-auto md:border-r-0 md:p-0"
      >
        {filterContent}
      </motion.div>
    </>
  );
}

interface FilterSectionProps {
  title: string;
  options: string[];
  selectedValues: string[];
  onToggle: (value: string) => void;
}

function FilterSection({ title, options, selectedValues, onToggle }: FilterSectionProps) {
  const sectionId = `filter-section-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <fieldset className="space-y-3">
      <legend id={sectionId} className="text-sm font-medium text-gray-300">{title}</legend>
      <div className="space-y-2" role="group" aria-labelledby={sectionId}>
        {options.map((option) => (
          <FilterCheckbox
            key={option}
            label={option}
            checked={selectedValues.includes(option)}
            onChange={() => onToggle(option)}
          />
        ))}
      </div>
    </fieldset>
  );
}

interface FilterCheckboxProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

function FilterCheckbox({ label, checked, onChange }: FilterCheckboxProps) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800 cursor-pointer"
        aria-label={`Filter by ${label}`}
      />
      <span className="text-sm text-gray-300 group-hover:text-white">{label}</span>
    </label>
  );
}

function calculateActiveCount(filters: FilterState): number {
  return (
    filters.categories.length +
    filters.languages.length +
    filters.tiers.length +
    filters.frameworks.length
  );
}
